import React, { useEffect, useRef, useState } from 'react';
import { IDSButton, IDSInput, IDSTextarea, IDSAccordion, IDSLink } from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import {
  fetchAdminContracts,
  createContract,
  updateContract,
  deleteContract,
  uploadContractResource,
  deleteContractResource,
  downloadContractResource,
} from '../../services/contractsAdminAPI';


// ── Contract form (create + edit) ─────────────────────────────────────────────
const ContractForm = ({ initial = {}, onSave, onCancel, submitLabel = 'Spara' }) => {
  const [contractName, setContractName] = useState(initial.contractName || '');
  const [version, setVersion] = useState(initial.version || '');
  const [description, setDescription] = useState(initial.description || '');
  const [documentationUrl, setDocumentationUrl] = useState(initial.documentationUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contractName.trim()) { setNameError('Kontraktnamn krävs.'); return; }
    setNameError(null);
    setLoading(true);
    setError(null);
    try {
      await onSave({ contractName: contractName.trim(), version: version.trim(), description: description.trim(), documentationUrl: documentationUrl.trim() || null });
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ background: 'var(--ids-palette-neutral-95)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '6px', padding: '1.25rem', marginBottom: '1rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <IDSInput label="Kontraktnamn *" type="text" value={contractName} onChange={e => { setContractName(e.target.value); if (nameError) setNameError(null); }} invalid={!!nameError} errorMsg={nameError} />
        <IDSInput label="Version" type="text" value={version} onChange={e => setVersion(e.target.value)} placeholder="t.ex. 3.0" />
      </div>
      <div className="ids-mb-3">
        <IDSTextarea label="Beskrivning" rows={2} value={description} onChange={e => setDescription(e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
      </div>
      <div className="ids-mb-3">
        <IDSInput label="Dokumentationslänk" type="url" value={documentationUrl} onChange={e => setDocumentationUrl(e.target.value)} placeholder="https://..." />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : submitLabel}</IDSButton>
        <IDSButton size="s" type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>
      <StatusAlert error={error} className="ids-mt-2" />
    </form>
  );
};

// ── Resource row ──────────────────────────────────────────────────────────────
const ResourceRow = ({ fileName, contractId, onDeleted }) => {
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadContractResource(contractId, fileName);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
    } finally {
      setDownloading(false);
    }
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await deleteContractResource(contractId, fileName);
      onDeleted(fileName);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setDeleting(false);
    }
  };

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort filen "${fileName}"?`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <li style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', marginBottom: '0.35rem' }}>
        <span style={{ flex: 1, fontSize: '0.875rem', wordBreak: 'break-all' }}>{fileName}</span>
        <IDSButton size="s" type="button" disabled={downloading} onClick={handleDownload}>
          {downloading ? '...' : 'Ladda ner'}
        </IDSButton>
        <IDSButton size="s" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}>
          {deleting ? '...' : 'Ta bort'}
        </IDSButton>
      </li>
      {error && <StatusAlert error={error} className="ids-mt-1" />}
    </>
  );
};

// ── Contract accordion ────────────────────────────────────────────────────────
const ContractAccordion = ({ contract, onContractUpdate, onContractDelete }) => {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [rowError, setRowError] = useState(null);
  const fileInputRef = useRef(null);

  const handleSave = async (data) => {
    const updated = await updateContract(contract.id, data);
    onContractUpdate(contract.id, { contractName: updated.contractName, version: updated.version, description: updated.description, documentationUrl: updated.documentationUrl });
    setEditing(false);
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    setRowError(null);
    try {
      await deleteContract(contract.id);
      onContractDelete(contract.id);
    } catch (err) {
      setRowError(err.message);
      setDeleting(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const savedName = await uploadContractResource(contract.id, file);
      onContractUpdate(contract.id, { files: [...(contract.files || []), savedName] });
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleFileDeleted = (fileName) => {
    onContractUpdate(contract.id, { files: contract.files.filter(f => f !== fileName) });
  };

  const fileCount = (contract.files || []).length;

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort kontraktet "${contract.contractName}${contract.version ? ' ' + contract.version : ''}"? Alla tillhörande resurser tas bort.`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <IDSAccordion
        headline={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3>{contract.contractName}</h3>
            {contract.version && (
              <span style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)' }}>v{contract.version}</span>
            )}
            <span style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-70)' }}>{fileCount} {fileCount === 1 ? 'fil' : 'filer'}</span>
          </div>
        }
        headlineSize="m"
        level={1}
      >
      <div>
        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <IDSButton size="s" type="button" onClick={() => setEditing(e => !e)}>
            {editing ? 'Avbryt redigering' : 'Redigera'}
          </IDSButton>
          <IDSButton size="s" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}>
            {deleting ? '...' : 'Ta bort'}
          </IDSButton>
        </div>

        {rowError && <p style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{rowError}</p>}

        {editing && (
          <ContractForm
            initial={{ contractName: contract.contractName, version: contract.version, description: contract.description, documentationUrl: contract.documentationUrl }}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
            submitLabel="Uppdatera"
          />
        )}

        {contract.description && (
          <p className="ids-mb-3" style={{ color: 'var(--ids-palette-neutral-50)', fontSize: '0.9rem' }}>{contract.description}</p>
        )}

        {contract.documentationUrl && (
          <p className="ids-mb-3" style={{ fontSize: '0.9rem' }}>
            <IDSLink underlined>
              <a href={contract.documentationUrl} target="_blank" rel="noopener noreferrer">
                <span>
                  Dokumentation
                  <span aria-hidden="true" className="ids-icon-external-link-small ids-icon--text-end" />
                </span>
              </a>
            </IDSLink>
          </p>
        )}

        {/* Resources */}
        <h4 className="ids-heading-s ids-mb-2">Resurser</h4>
        {fileCount === 0 ? (
          <p className="ids-mb-3" style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.875rem' }}>Inga filer uppladdade ännu.</p>
        ) : (
          <ul style={{ padding: 0, margin: '0 0 0.75rem 0' }}>
            {contract.files.map(f => (
              <ResourceRow key={f} fileName={f} contractId={contract.id} onDeleted={handleFileDeleted} />
            ))}
          </ul>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <IDSButton size="s" type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            {uploading ? 'Laddar upp...' : '+ Ladda upp fil'}
          </IDSButton>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
          {uploadError && <span style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.8rem' }}>{uploadError}</span>}
        </div>
      </div>
    </IDSAccordion>
    </>
  );
};

// ── Main layout ───────────────────────────────────────────────────────────────
const ContractsAdminLayout = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminContracts();
        setContracts(data);
      } catch (err) {
        setError(err.message || "Något gick fel. Försök igen.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleContractCreated = async (data) => {
    const created = await createContract(data);
    setContracts(prev => [...prev, { ...created, files: [] }]);
    setShowCreateForm(false);
  };

  const handleContractUpdate = (contractId, patch) => {
    setContracts(prev => prev.map(c => c.id === contractId ? { ...c, ...patch } : c));
  };

  const handleContractDelete = (contractId) => {
    setContracts(prev => prev.filter(c => c.id !== contractId));
  };

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '860px', margin: '2rem auto' }}>
      <h1 className="ids-heading-l ids-mb-2">TK – Tjänstekontrakt</h1>

      <section className="ids-mt-6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="ids-heading-m">Tjänstekontrakt</h2>
          {!showCreateForm && (
            <IDSButton size="s" type="button" onClick={() => setShowCreateForm(true)}>+ lägg till nytt tjänstekontrakt</IDSButton>
          )}
        </div>

        {showCreateForm && (
          <ContractForm onSave={handleContractCreated} onCancel={() => setShowCreateForm(false)} submitLabel="Skapa" />
        )}

        {loading && <p>Laddar...</p>}
        <StatusAlert error={error} />
        {!loading && !error && contracts.length === 0 && (
          <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga kontrakt skapade ännu.</p>
        )}

        {contracts.map(contract => (
          <ContractAccordion
            key={contract.id}
            contract={contract}
            onContractUpdate={handleContractUpdate}
            onContractDelete={handleContractDelete}
          />
        ))}
      </section>
    </main>
  );
};

export default ContractsAdminLayout;
